const IMG = '/images/glp1';

const page = {
  slug: 'glp1-meal-plan',
  title: 'GLP-1 Meal Plan',
  hero: {
    title: 'GLP-1 Meal Plan',
    subtitle: 'SQUARE FARE X AMY SHAPIRO, RD, CDN',
    subtitleBelow: true,
    image: `${IMG}/hero.webp`,
  },
  blocks: [
    // Write-up beside the subscriptions it describes, so Subscribe stays on screen.
    {
      type: 'textCards',
      variant: 'square',
      copy: {
        heading: 'Your GLP-1 Meal Plan',
        paragraphs: [
          'GLP-1 medications like Ozempic®, Wegovy®, Mounjaro®, and Zepbound® are powerful tools for supporting weight loss and metabolic health. But when appetite drops, it becomes even more important to nourish intentionally, supporting energy, digestion, and lean muscle.',
          "At Square Fare, we've designed a GLP-1-friendly meal plan to meet your nutritional needs without the guesswork. In partnership with Registered Dietitian Amy Shapiro, every meal is made from scratch and designed to be easy to digest, satisfying, and nutritionally complete.",
          'All meals are gluten-free, low sodium, free of added sugar, cooked only in olive oil—and built around a macronutrient profile of 30% carbohydrates, 40% protein, 30% fat, delivering 30–70 grams of protein per meal.',
          'Choose the number of breakfasts, lunches, dinners, and snacks you would like every week - all designed for smaller appetites but big on nourishment',
          "After you subscribe: we will share your first week's menu for your review and also connect you with GLP-1 nutrition expert Amy Shapiro if you choose a consultation with her.",
        ],
      },
      items: [
        {
          heading: 'Select Your Meal Plan',
          title: 'GLP-1 Meal Plan',
          price: 'from $129.99 every week',
          lines: [
            'Prepared meals optimized for your GLP-1 medication journey',
            'Select the number of days you would like breakfast, lunch and dinner. Snacks are optional.',
          ],
          image: `${IMG}/card-meal-plan.webp`,
          imageAlt: 'Three GLP-1 meal containers with grilled protein, grains and vegetables',
          cta: {
            label: 'Subscribe',
            href: 'https://www.getsquarefare.com/this-weeks-meals/p/tailored-meals-a4hy3-s4fng-dee9f-px65n-8nes4-dd9gr-gr8cw-7m7xy-l885e-w4g9r-pwshw-mjsds-3wy33-dz859-fh39d-2t57k-tl3w9-ast28-3lw63',
          },
        },
        {
          heading: 'Get Expert Support',
          title: 'Monthly Consultation with Expert Amy Shapiro',
          price: '$130.00 every month',
          lines: [
            'A monthly 20 mins call with Amy Shapiro, RD CDN to go over your need and any changes that may be needed month over month',
            'Next: we will reach out to setup your first consultation with Amy Shapiro and share your menu for your first week for your review.',
          ],
          image: `${IMG}/card-consultation.webp`,
          imageAlt: 'Portrait of Amy Shapiro, MS, RD, CDN',
          cta: {
            label: 'Subscribe',
            href: 'https://www.getsquarefare.com/this-weeks-meals/p/tailored-meals-a4hy3-s4fng-dee9f-px65n-8nes4-dd9gr-gr8cw-7m7xy-l885e-w4g9r-pwshw-mjsds-3wy33-dz859-fh39d-2t57k-tl3w9-ast28-3lw63-52ase',
          },
        },
      ],
    },
    {
      type: 'gallery',
      tone: 'warm',
      variant: 'bleed',
      heading: 'Food Gallery',
      items: [
        {
          image: `${IMG}/gallery-1.webp`,
          imageAlt: 'A colorful plate of Mexican food with grilled vegetables, black beans and lime',
        },
        {
          image: `${IMG}/gallery-2.webp`,
          imageAlt: 'A bowl of grilled shrimp, roasted potatoes, sliced zucchini and cucumber',
        },
        {
          image: `${IMG}/gallery-3.webp`,
          imageAlt: 'Mixed salad bowl with greens, cherry tomatoes, diced mango and a poached egg',
        },
        {
          image: `${IMG}/gallery-4.webp`,
          imageAlt: 'Buddha bowl with broccoli, carrots, quinoa, tofu, spinach and lime',
        },
        {
          image: `${IMG}/gallery-5.webp`,
          imageAlt: 'Buddha bowl with grilled salmon, diced mango, cucumber slices and shredded cabbage',
        },
        {
          image: `${IMG}/gallery-6.webp`,
          imageAlt: 'Grilled chicken with vegetables, radishes, roasted potatoes and cucumber',
        },
        {
          image: `${IMG}/gallery-7.webp`,
          imageAlt: 'Bowl of chocolate walnut energy bites on a dark surface',
        },
      ],
    },
    { type: 'heading', text: 'Sample 3-day Meal Plan', align: 'center' },
    // One row, so meals can be compared across the three days.
    {
      type: 'listRow',
      ordered: false,
      items: [
        {
          heading: 'Day 1',
          items: [
            '🍳 Breakfast: Overnight Oats with Chia and Fresh Berries — Rolled oats, almond milk, chia seeds, blueberries, strawberries, and cinnamon',
            '🥗 Lunch: Lean Power Bowl with Turkey Meatballs — Roasted squash, broccolini, tomato sauce, pine nuts',
            '🍽 Dinner: Skin Renew Bowl with Wild-Caught Cod — Charred cabbage, squash, shaved fennel, grapefruit-mint vinaigrette',
            '🍪 Snack: Meyer Lemon Energy Bites (2 pieces) — Dates, lemon zest, coconut, almond flour',
          ],
        },
        {
          heading: 'Day 2',
          items: [
            '🍳 Breakfast: Morning Energy Bowl with Scrambled Eggs and Bok Choy — Sorghum, pickled radish, scallion-sesame sauce',
            '🥗 Lunch: Focus Fuel Bowl with Salmon — Corn, spinach, mushrooms, red pepper, balsamic-chive vinaigrette',
            '🍽 Dinner: Fiber Strength Bowl with Chickpeas — Edamame noodles, basil arugula pesto, shaved parmesan',
            '🥜 Snack: Healthy Trail Mix (¼ cup) — Chickpeas, almonds, cranberries, pumpkin seeds, coconut shreds',
          ],
        },
        {
          heading: 'Day 3',
          items: [
            '🍳 Breakfast: Greek Yogurt Parfait with Homemade Muesli and Nectarines — Plain yogurt, house-made granola, seasonal stone fruit',
            '🥗 Lunch: Metabolic Boost Bowl with Sofrito Chicken — Brown rice, kale, crispy plantain chips, tomato sofrito',
            '🍽 Dinner: Gut Harmony Bowl with Sesame-Ginger Tofu — Edamame noodles, bok choy, sesame-chili dressing',
            '🍫 Snack: High-Protein Peanut Butter Cup (1 piece) — Low sugar, protein-rich indulgence',
          ],
        },
      ],
    },
    {
      type: 'split',
      image: `${IMG}/testimonial-amy.webp`,
      imageAlt: 'Portrait of Amy Shapiro, MS, RD, CDN, founder of Real Nutrition',
      imageSide: 'left',
      groups: [
        {
          heading: 'Your Nutrition Expert',
          paragraphs: [
            'Amy Shapiro MS, RD, CDN, is the founder and director of Real Nutrition, a NYC-based private practice dedicated to healthfully and successfully guiding clients to their optimal nutrition, weight, and overall wellness. She is internationally recognized for her individualized, lifestyle-focused approach, which integrates realistic food plans, smart eating habits, and active living. Through encouragement, education, and the right "tricks of the trade", Amy believes that anyone can achieve their nutritional goals while still enjoying the foods and flavors they crave.',
          ],
        },
      ],
    },
    {
      type: 'ctaGroup',
      heading: 'Still Have Questions?',
      actions: [
        { label: 'Contact Us', to: '/contact-form-general' },
        { label: 'Visit FAQ', to: '/faqs'},
      ],
    },
  ],
};

export default page;
